import { useCallback } from 'react'
import { usePostHog } from 'posthog-js/react'
import { useAppSelector } from '@/redux/hooks'
import { useIsReverStaff } from '@/hooks/use-is-rever-staff'
import { useChatContext } from './chat-context'
import { promptNameFromMarker } from './suggested-prompts'
import type { ChatFeedbackRating } from './types'

// What triggered a send: the composer, a predefined prompt, or a follow-up chip.
export type ChatMessageSource = 'composer' | 'suggested_prompt' | 'followup'

// Enough of the question to read intent, without a pasted blob as a property.
const MESSAGE_MAX_LENGTH = 500
// The reply is the thing being judged, so it gets more room than the question —
// still capped, since a rated answer can be a long table.
const ANSWER_MAX_LENGTH = 1000

interface SentMessage {
  content: string
  source: ChatMessageSource
  // How many turns preceded it, so a one-shot question reads apart from a thread.
  turnIndex: number
}

interface ClickedLink {
  label: string
  href: string
}

// One rated exchange. The question travels with the answer because a rating on
// its own says nothing about what the assistant got right or wrong.
interface RatedReply {
  rating: ChatFeedbackRating
  question: string
  answer: string
  // Set only when the question came from a predefined prompt.
  promptName?: string
  turnIndex: number
}

interface CommentedReply extends RatedReply {
  comment: string
}

export interface AssistantTracking {
  trackOpened: () => void
  trackMessageSent: (message: SentMessage) => void
  trackExportClicked: (link: ClickedLink) => void
  trackFeedback: (reply: RatedReply) => void
  // Separate from the rating so the rate stays complete (every click counts)
  // while only written feedback is worth forwarding to a Slack channel.
  trackFeedbackComment: (reply: CommentedReply) => void
}

// Usage tracking for the assistant. Every event carries the screen context it
// was fired from, so analytics and shipping-costs usage can be told apart.
export function useAssistantTracking(): AssistantTracking {
  const posthog = usePostHog()
  const { activeContext } = useChatContext()
  const context = activeContext?.tab
  const ecommerceId = useAppSelector(
    (s) => s.settingsApi.settings.response.ecommerce_id
  )
  const user = useAppSelector((s) => s.userApi.getMe.response.user?.name)
  // Tagged, not dropped: our own dogfooding stays visible but filterable.
  const isReverUser = useIsReverStaff()

  const capture = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      posthog?.capture(event, {
        context,
        ecommerceId,
        user,
        isReverUser,
        ...properties
      })
    },
    [posthog, context, ecommerceId, user, isReverUser]
  )

  const trackOpened = useCallback(
    () => capture('AI Assistant Opened'),
    [capture]
  )

  const trackMessageSent = useCallback(
    ({ content, source, turnIndex }: SentMessage) =>
      capture('AI Assistant Message Sent', {
        message: content.slice(0, MESSAGE_MAX_LENGTH),
        source,
        // Set only for a predefined prompt, whose message travels as a marker.
        promptName: promptNameFromMarker(content),
        turnIndex
      }),
    [capture]
  )

  const trackExportClicked = useCallback(
    ({ label, href }: ClickedLink) =>
      capture('AI Assistant Export Clicked', { label, path: urlPath(href) }),
    [capture]
  )

  const trackFeedback = useCallback(
    (reply: RatedReply) => capture('AI Assistant Feedback', ratedProps(reply)),
    [capture]
  )

  const trackFeedbackComment = useCallback(
    ({ comment, ...reply }: CommentedReply) =>
      capture('AI Assistant Feedback Comment', {
        ...ratedProps(reply),
        comment: comment.slice(0, MESSAGE_MAX_LENGTH)
      }),
    [capture]
  )

  return {
    trackOpened,
    trackMessageSent,
    trackExportClicked,
    trackFeedback,
    trackFeedbackComment
  }
}

function ratedProps({ question, answer, ...rest }: RatedReply) {
  return {
    ...rest,
    question: question.slice(0, MESSAGE_MAX_LENGTH),
    answer: answer.slice(0, ANSWER_MAX_LENGTH)
  }
}

// The download is a presigned S3 link: keep the path, never the signed query.
function urlPath(href: string): string | undefined {
  try {
    return new URL(href).pathname
  } catch {
    return undefined
  }
}
